import React, { useEffect, useMemo, useState } from "react";
import ReactSelect from "react-select";
import { X, Sparkles, AlertTriangle, Flame } from "lucide-react";
import { toast } from "react-toastify";
import ModalPortal from "../ModalPortal";
import NotificationDateTimePicker from "./NotificationDateTimePicker";
import notificationService from "../../services/notificationService";
import modelService from "../../services/modelService";
import { useDashboardUI } from "../../context/GlobalContext";
import { singleSelectStyles } from "../../helpers/constant";

// Notification links always open in a new tab — a value with no scheme
// (e.g. an admin typing "status.io" instead of "https://status.io") must
// never reach the FE as something a browser could resolve as a relative
// path against the current page. Normalize here, at the source.
const ensureAbsoluteUrl = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  try {
    new URL(trimmed);
    return trimmed;
  } catch {
    return `https://${trimmed.replace(/^\/+/, "")}`;
  }
};

// Mirrors the backend's own link validation (_validate_notification_link in
// app/schemas/notifications/broadcast.py) — ensureAbsoluteUrl above will
// happily turn "notaurl" into "https://notaurl", which still parses as a
// URL but isn't a real destination. Catch that here instead of only after
// a 422 round-trip: scheme must be http(s), and the host needs an actual
// dot (rejects bare tokens like "notaurl", not real domains like
// "localhost" — acceptable, since this is a published-content link, never
// meant to point at a local dev server).
const isValidNotificationUrl = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return true; // empty is fine here; requiredness is checked separately
  try {
    const parsed = new URL(ensureAbsoluteUrl(trimmed));
    return (parsed.protocol === "http:" || parsed.protocol === "https:") && parsed.hostname.includes(".");
  } catch {
    return false;
  }
};

// Shared by validate() (on submit) and the Link field's onBlur (live, as
// the admin fills the form) so both report the exact same thing.
const linkError = (link, category) => {
  if (category === "release_note" && !link.trim()) {
    // /release-notes no longer exists in-app — the HelpKit/Notion link is
    // the only place the write-up lives, so a release note without one
    // goes nowhere when clicked.
    return "Link is required for release notes (the published Notion/HelpKit page)";
  }
  if (link.trim() && !isValidNotificationUrl(link)) {
    return "Enter a valid URL, e.g. https://example.com";
  }
  return null;
};

// Same idea as linkError below — shared by validate() (on submit) and each
// picker's onChange (live), so a past date/time is flagged the instant it's
// picked rather than silently accepted until the admin hits submit. The
// picker's own day-grid only disables past *days*, not a past *time* on the
// currently-selected day (e.g. picking "today" then an earlier hour), so
// this is still needed even with minDate wired into the picker.
const deprecationAtError = (date) => {
  if (!date) return "Deprecation date/time is required";
  if (date.getTime() <= Date.now()) return "Deprecation date/time must be in the future";
  return null;
};

// For model_deprecation, expiry also has to land after the deprecation
// moment itself — otherwise the notice disappears from user views before
// (or the same instant) the thing it's warning about actually happens.
// deprecationAt is only ever passed for that category; null everywhere else.
const expiresAtError = (date, deprecationAt = null) => {
  if (!date) return null;
  if (date.getTime() <= Date.now()) return "Expiry must be in the future";
  if (deprecationAt && date.getTime() <= deprecationAt.getTime()) {
    return "Expiry must be after the deprecation date/time";
  }
  return null;
};

const inputClass = (hasError) =>
  `w-full rounded-xl border ${
    hasError ? "border-red-400" : "border-gray-200"
  } bg-gray-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/35 focus:border-blue-500 transition`;

// NotificationDateTimePicker always shows/edits a Date's LOCAL wall-clock
// digits — there's no built-in way to enter a value "as UTC". To support
// that, we
// feed it a "fake" Date shifted by the browser's own UTC offset so its
// LOCAL digits equal the real value's UTC digits, then reverse the shift
// when it changes. The underlying formData value is always the one true
// instant; only what's displayed/edited flips between the two.
const toUtcDisplayDate = (date) => (date ? new Date(date.getTime() + date.getTimezoneOffset() * 60000) : null);
const fromUtcDisplayDate = (date) => (date ? new Date(date.getTime() - date.getTimezoneOffset() * 60000) : null);

const formatUtc = (date) => `${date.toISOString().replace("T", " ").slice(0, 16)} UTC`;

// The backend returns naive-UTC ISO strings (no Z/offset) -- new Date(...)
// would otherwise parse that as local time, same gotcha as everywhere else
// this convention shows up in this app (see notificationMeta.js's
// formatUtcToLocal). Only used when loading an existing notification into
// the form for editing.
const parseUtcIsoToDate = (iso) => {
  if (!iso) return null;
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasZone ? iso : `${iso}Z`);
};

// Local/UTC toggle shown above a datetime picker, plus a caption showing
// the other timezone's equivalent value so both are always visible.
const TimeModeToggle = ({ mode, onChange }) => (
  <div className="flex rounded-full border border-gray-200 p-0.5 text-xs">
    {["local", "utc"].map((m) => (
      <button
        key={m}
        type="button"
        onClick={() => onChange(m)}
        className={`px-2.5 py-1 rounded-full font-medium transition-colors ${
          mode === m ? "bg-[#2B3674] text-white" : "text-gray-500"
        }`}
      >
        {m === "local" ? "Local" : "UTC"}
      </button>
    ))}
  </div>
);

const CATEGORY_OPTIONS = [
  {
    value: "release_note",
    label: "Release Note",
    desc: "New features, fixes — informational",
    icon: Sparkles,
  },
  {
    value: "model_deprecation",
    label: "Model Deprecation",
    desc: "Action-oriented, may need acknowledgement",
    icon: AlertTriangle,
  },
  {
    value: "emergency",
    label: "Emergency",
    desc: "Incidents, outages — high urgency",
    icon: Flame,
  },
];

const SEVERITY_OPTIONS = [
  { value: "info", label: "Info" },
  { value: "warning", label: "Warning" },
  { value: "critical", label: "Critical" },
];

const initialFormData = {
  category: "release_note",
  title: "",
  body: "",
  link: "",
  version: "",
  affected_models: [],
  deprecation_at: null,
  severity: "info",
  dismissible: true,
  audience_type: "ALL",
  target_facility_ids: [],
  publish_choice: "now", // draft | now | schedule
  scheduled_for: null,
  expires_at: null,
};

// Drawer, not a centered modal like CreateIncidentModal — this form has
// enough conditional content (category-specific fields, targeting, publish
// timing) to want the extra width/height. See
// docs/notifications-frd-and-design.md §10.3.
const CreateNotificationDrawer = ({ open, onClose, editTarget = null }) => {
  const { facilityList } = useDashboardUI();
  const isEditMode = Boolean(editTarget);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modelConfigs, setModelConfigs] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [deprecationTimeMode, setDeprecationTimeMode] = useState("local");
  const [scheduledTimeMode, setScheduledTimeMode] = useState("local");
  const [expiresTimeMode, setExpiresTimeMode] = useState("local");

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setDeprecationTimeMode("local");
    setScheduledTimeMode("local");
    setExpiresTimeMode("local");
    if (editTarget) {
      // Only draft/scheduled notifications can be edited (backend-enforced —
      // see PUT /admin/notifications/{id}); category/status themselves
      // aren't part of NotificationUpdate, so they're carried here for
      // display only, never sent back on save.
      setFormData({
        category: editTarget.category,
        title: editTarget.title || "",
        body: editTarget.body || "",
        link: editTarget.link || "",
        version: editTarget.version || "",
        affected_models: editTarget.affected_models || [],
        deprecation_at: parseUtcIsoToDate(editTarget.deprecation_at),
        severity: editTarget.severity || "info",
        dismissible: editTarget.dismissible ?? true,
        // Force ALL for model_deprecation even if the loaded row somehow
        // predates this restriction — targeting a global config change per
        // facility was never really meaningful, and saving now re-asserts it.
        audience_type: editTarget.category === "model_deprecation" ? "ALL" : editTarget.audience_type || "ALL",
        target_facility_ids: editTarget.category === "model_deprecation" ? [] : editTarget.target_facility_ids || [],
        publish_choice: editTarget.status === "scheduled" ? "schedule" : "draft",
        scheduled_for: parseUtcIsoToDate(editTarget.scheduled_for),
        expires_at: parseUtcIsoToDate(editTarget.expires_at),
      });
    } else {
      setFormData(initialFormData);
    }
  }, [open, editTarget]);

  // Lazy-fetch once, the first time the drawer is open on the
  // model_deprecation category — GlobalModelConfig rarely changes mid-session
  // so there's no need to refetch on every open.
  useEffect(() => {
    if (
      open &&
      formData.category === "model_deprecation" &&
      modelConfigs.length === 0 &&
      !modelsLoading
    ) {
      setModelsLoading(true);
      modelService
        .getGlobalModelConfigs()
        .then((data) => setModelConfigs(data || []))
        .catch(() => toast.error("Failed to load models"))
        .finally(() => setModelsLoading(false));
    }
  }, [open, formData.category, modelConfigs.length, modelsLoading]);

  const modelOptions = useMemo(
    () =>
      modelConfigs
        // Only show models that are still enabled — a deprecation notice
        // targets what's active, not what's already turned off.
        .filter((m) => m.is_active)
        .map((m) => ({
          value: m.id,
          label: m.config_name,
        })),
    [modelConfigs]
  );

  const facilityOptions = useMemo(
    () =>
      (facilityList || []).map((f) => ({
        value: f.facility_number,
        label: f.name,
      })),
    [facilityList]
  );

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  // model_deprecation deactivates GlobalModelConfig rows — a global config
  // change, not something scoped to specific facilities — so targeting is
  // forced to ALL and any prior facility selection is dropped.
  const handleCategoryChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      category: value,
      ...(value === "model_deprecation" ? { audience_type: "ALL", target_facility_ids: [] } : {}),
    }));
    if (errors.target_facility_ids) setErrors((prev) => ({ ...prev, target_facility_ids: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (formData.audience_type === "FACILITY" && formData.target_facility_ids.length === 0) {
      newErrors.target_facility_ids = "Select at least one facility";
    }
    if (formData.publish_choice === "schedule" && !formData.scheduled_for) {
      newErrors.scheduled_for = "Pick a date/time to schedule for";
    }
    const linkErr = linkError(formData.link, formData.category);
    if (linkErr) newErrors.link = linkErr;
    if (formData.category === "model_deprecation") {
      if (formData.affected_models.length === 0) {
        newErrors.affected_models = "Select at least one affected model";
      }
      const deprecationErr = deprecationAtError(formData.deprecation_at);
      if (deprecationErr) newErrors.deprecation_at = deprecationErr;
    }
    const expiresErr = expiresAtError(
      formData.expires_at,
      formData.category === "model_deprecation" ? formData.deprecation_at : null
    );
    if (expiresErr) newErrors.expires_at = expiresErr;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildCreatePayload = () => {
    const status =
      formData.publish_choice === "draft"
        ? "draft"
        : formData.publish_choice === "schedule"
        ? "scheduled"
        : "published";

    const payload = {
      category: formData.category,
      title: formData.title.trim(),
      body: formData.body.trim() || null,
      link: formData.link.trim() ? ensureAbsoluteUrl(formData.link) : null,
      audience_type: formData.audience_type,
      status,
    };

    if (formData.audience_type === "FACILITY") {
      payload.target_facility_ids = formData.target_facility_ids;
    }
    if (formData.category === "release_note") {
      payload.version = formData.version.trim() || null;
    }
    if (formData.category === "model_deprecation") {
      payload.affected_models = formData.affected_models;
      payload.deprecation_at = formData.deprecation_at
        ? formData.deprecation_at.toISOString()
        : null;
    }
    if (formData.category === "emergency") {
      payload.severity = formData.severity;
      payload.dismissible = formData.severity === "critical" ? formData.dismissible : true;
    }
    if (status === "scheduled") {
      payload.scheduled_for = formData.scheduled_for.toISOString();
    }
    if (formData.expires_at) {
      payload.expires_at = formData.expires_at.toISOString();
    }

    return payload;
  };

  // category/status aren't part of NotificationUpdate (see PUT
  // /admin/notifications/{id}) -- category never changes, and status only
  // changes via the dedicated Publish now/Archive actions, not this form.
  const buildUpdatePayload = () => {
    const payload = {
      title: formData.title.trim(),
      body: formData.body.trim() || null,
      link: formData.link.trim() ? ensureAbsoluteUrl(formData.link) : null,
      audience_type: formData.audience_type,
    };

    if (formData.audience_type === "FACILITY") {
      payload.target_facility_ids = formData.target_facility_ids;
    }
    if (formData.category === "release_note") {
      payload.version = formData.version.trim() || null;
    }
    if (formData.category === "model_deprecation") {
      payload.affected_models = formData.affected_models;
      payload.deprecation_at = formData.deprecation_at
        ? formData.deprecation_at.toISOString()
        : null;
    }
    if (formData.category === "emergency") {
      payload.severity = formData.severity;
      payload.dismissible = formData.severity === "critical" ? formData.dismissible : true;
    }
    if (editTarget?.status === "scheduled") {
      payload.scheduled_for = formData.scheduled_for ? formData.scheduled_for.toISOString() : null;
    }
    // Explicit null (not omitted) so clearing an existing expiry in edit
    // mode actually clears it — unlike create, there's a prior value here
    // that omitting the field would just leave untouched.
    payload.expires_at = formData.expires_at ? formData.expires_at.toISOString() : null;

    return payload;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        await notificationService.updateNotification(editTarget.id, buildUpdatePayload());
        toast.success("Notification updated");
      } else {
        await notificationService.createNotification(buildCreatePayload());
        toast.success(
          formData.publish_choice === "draft"
            ? "Draft saved"
            : formData.publish_choice === "schedule"
            ? "Notification scheduled"
            : "Notification published"
        );
      }
      onClose(true);
    } catch (error) {
      toast.error(isEditMode ? "Failed to update notification" : "Failed to save notification");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderScheduledForField = () => (
    <div>
      <div className="flex items-center justify-end mb-1.5">
        <TimeModeToggle mode={scheduledTimeMode} onChange={setScheduledTimeMode} />
      </div>
      <NotificationDateTimePicker
        selected={
          scheduledTimeMode === "utc" ? toUtcDisplayDate(formData.scheduled_for) : formData.scheduled_for
        }
        onChange={(date) =>
          handleChange("scheduled_for", scheduledTimeMode === "utc" ? fromUtcDisplayDate(date) : date)
        }
        hasError={Boolean(errors.scheduled_for)}
        placeholder="Select date and time"
      />
      {errors.scheduled_for && <p className="text-red-500 text-xs mt-1">{errors.scheduled_for}</p>}
      {formData.scheduled_for && (
        <p className="text-xs text-gray-500 mt-1">
          {scheduledTimeMode === "utc"
            ? `Local: ${formData.scheduled_for.toLocaleString()}`
            : formatUtc(formData.scheduled_for)}
        </p>
      )}
    </div>
  );

  if (!open) return null;

  const audienceLabel =
    formData.audience_type === "ALL"
      ? "All facilities"
      : `${formData.target_facility_ids.length} facilit${formData.target_facility_ids.length === 1 ? "y" : "ies"}`;

  const publishLabel =
    formData.publish_choice === "draft"
      ? "Saved as draft"
      : formData.publish_choice === "schedule"
      ? "Scheduled"
      : "Immediately";

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-50 bg-black bg-opacity-40 transition-opacity"
        onClick={() => !isSubmitting && onClose(false)}
      />

      <div className="fixed top-0 right-0 z-50 h-full w-full max-w-[720px] bg-white shadow-2xl flex flex-col animate-slide-in-right">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">
            {isEditMode ? "Edit Notification" : "New Notification"}
          </h2>
          <button
            onClick={() => !isSubmitting && onClose(false)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="flex flex-col gap-6">
            {/* Category — fixed at creation, not editable (not part of
                NotificationUpdate), so edit mode shows only the current
                one, inert rather than a clickable-looking picker. */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Category
              </h3>
              <div className="space-y-2">
                {CATEGORY_OPTIONS.filter((opt) => !isEditMode || opt.value === formData.category).map(
                  ({ value, label, desc, icon: Icon }) => (
                    <div
                      key={value}
                      onClick={() => !isEditMode && handleCategoryChange(value)}
                      className={`flex items-start gap-3 px-4 py-3 rounded-lg border transition-colors ${
                        isEditMode ? "cursor-default" : "cursor-pointer"
                      } ${
                        formData.category === value
                          ? "border-[#2B3674] bg-[#2B3674]/5"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <Icon className="h-5 w-5 mt-0.5 text-gray-500 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>

            <div className="border-b border-gray-200" />

            {/* Content */}
            <div className="flex flex-col gap-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Content
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Title <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  maxLength={200}
                  placeholder="e.g. Voice agents, faster search"
                  className={inputClass(errors.title)}
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
              </div>

              {formData.category !== "release_note" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Body</label>
                  <textarea
                    value={formData.body}
                    onChange={(e) => handleChange("body", e.target.value)}
                    rows={5}
                    placeholder="Full markdown — a trimmed preview of this shows in the panel/banner"
                    className={`${inputClass(false)} resize-none`}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Link{" "}
                  {formData.category === "release_note" ? (
                    <span className="text-red-400">*</span>
                  ) : (
                    <span className="text-gray-400">(optional)</span>
                  )}
                </label>
                <input
                  type="text"
                  value={formData.link}
                  onChange={(e) => handleChange("link", e.target.value)}
                  onBlur={() => {
                    const linkErr = linkError(formData.link, formData.category);
                    setErrors((prev) => ({ ...prev, link: linkErr }));
                  }}
                  placeholder="https://… “Learn more” destination"
                  className={inputClass(errors.link)}
                />
                {errors.link && <p className="text-red-500 text-xs mt-1">{errors.link}</p>}
                {formData.category === "release_note" && (
                  <p className="text-xs text-gray-500 mt-1.5">
                    Release notes live in Notion, synced to{" "}
                    <a
                      href="https://docs.genserve.ai/"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      docs.genserve.ai
                    </a>{" "}
                    — not in this form.{" "}
                    {process.env.REACT_APP_NOTION_WORKSPACE_URL && (
                      <>
                        <a
                          href={process.env.REACT_APP_NOTION_WORKSPACE_URL}
                          target="_blank"
                          rel="noreferrer"
                          className="underline font-medium"
                        >
                          Create the page in Notion
                        </a>
                        , then paste its published docs.genserve.ai link above once HelpKit syncs it.
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>

            <div className="border-b border-gray-200" />

            {/* Category-specific */}
            {formData.category === "release_note" && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Release Note Details
                </h3>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Version</label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => handleChange("version", e.target.value)}
                  placeholder="v2.4.0"
                  className={inputClass(false)}
                />
              </div>
            )}

            {formData.category === "model_deprecation" && (
              <div className="flex flex-col gap-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Model Deprecation Details
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Affected models <span className="text-red-400">*</span>
                  </label>
                  <ReactSelect
                    isMulti
                    isLoading={modelsLoading}
                    options={modelOptions}
                    value={modelOptions.filter((o) => formData.affected_models.includes(o.value))}
                    onChange={(selected) =>
                      handleChange("affected_models", (selected || []).map((o) => o.value))
                    }
                    placeholder="Select affected models"
                    classNamePrefix="react-select"
                    closeMenuOnSelect={false}
                    styles={{
                      ...singleSelectStyles,
                      control: (base, state) => ({
                        ...singleSelectStyles.control(base, state),
                        borderColor: errors.affected_models
                          ? "#f87171"
                          : singleSelectStyles.control(base, state).borderColor,
                      }),
                    }}
                  />
                  {errors.affected_models && (
                    <p className="text-red-500 text-xs mt-1">{errors.affected_models}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1.5">
                    These exact configs are automatically deactivated (is_active set to false) at
                    the date and time below — a job is scheduled for that exact moment, not a
                    periodic scan.
                  </p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-gray-700">
                      Deprecation date &amp; time <span className="text-red-400">*</span>
                    </label>
                    <TimeModeToggle mode={deprecationTimeMode} onChange={setDeprecationTimeMode} />
                  </div>
                  <NotificationDateTimePicker
                    selected={
                      deprecationTimeMode === "utc"
                        ? toUtcDisplayDate(formData.deprecation_at)
                        : formData.deprecation_at
                    }
                    onChange={(date) => {
                      const realDate = deprecationTimeMode === "utc" ? fromUtcDisplayDate(date) : date;
                      handleChange("deprecation_at", realDate);
                      setErrors((prev) => ({
                        ...prev,
                        deprecation_at: deprecationAtError(realDate),
                        // Moving the deprecation time can flip an
                        // already-valid expiry (e.g. dragging it past a
                        // previously-fine expires_at) — re-check it too.
                        expires_at: expiresAtError(formData.expires_at, realDate),
                      }));
                    }}
                    hasError={Boolean(errors.deprecation_at)}
                    placeholder="Select date and time"
                    minDate={new Date()}
                  />
                  {errors.deprecation_at && (
                    <p className="text-red-500 text-xs mt-1">{errors.deprecation_at}</p>
                  )}
                  {formData.deprecation_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      {deprecationTimeMode === "utc"
                        ? `Local: ${formData.deprecation_at.toLocaleString()}`
                        : formatUtc(formData.deprecation_at)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {formData.category === "emergency" && (
              <div className="flex flex-col gap-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Emergency Details
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Severity</label>
                  <div className="flex gap-2">
                    {SEVERITY_OPTIONS.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleChange("severity", value)}
                        className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          formData.severity === value
                            ? "border-[#2B3674] bg-[#2B3674]/5 text-[#2B3674]"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                {formData.severity === "critical" && (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.dismissible}
                      onChange={(e) => handleChange("dismissible", e.target.checked)}
                      className="accent-[#2B3674]"
                    />
                    Dismissible
                  </label>
                )}
              </div>
            )}

            <div className="border-b border-gray-200" />

            {/* Targeting */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Targeting
              </h3>
              {formData.category === "model_deprecation" ? (
                <p className="text-sm text-gray-500">
                  Always all facilities — model deprecation deactivates a global model config, not
                  something scoped per facility.
                </p>
              ) : (
                <div className="flex gap-4 mb-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="audience_type"
                      checked={formData.audience_type === "ALL"}
                      onChange={() => handleChange("audience_type", "ALL")}
                      className="accent-[#2B3674]"
                    />
                    All facilities
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="audience_type"
                      checked={formData.audience_type === "FACILITY"}
                      onChange={() => handleChange("audience_type", "FACILITY")}
                      className="accent-[#2B3674]"
                    />
                    Specific facilities
                  </label>
                </div>
              )}
              {formData.category !== "model_deprecation" && formData.audience_type === "FACILITY" && (
                <ReactSelect
                  isMulti
                  options={facilityOptions}
                  value={facilityOptions.filter((o) => formData.target_facility_ids.includes(o.value))}
                  onChange={(selected) =>
                    handleChange("target_facility_ids", (selected || []).map((o) => o.value))
                  }
                  placeholder="Select facilities"
                  classNamePrefix="react-select"
                  closeMenuOnSelect={false}
                  styles={{
                    ...singleSelectStyles,
                    control: (base, state) => ({
                      ...singleSelectStyles.control(base, state),
                      borderColor: errors.target_facility_ids
                        ? "#f87171"
                        : singleSelectStyles.control(base, state).borderColor,
                    }),
                  }}
                />
              )}
              {errors.target_facility_ids && (
                <p className="text-red-500 text-xs mt-1">{errors.target_facility_ids}</p>
              )}
            </div>

            <div className="border-b border-gray-200" />

            {/* Expiry */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Expires <span className="text-gray-400 normal-case">(optional)</span>
                </h3>
                <TimeModeToggle mode={expiresTimeMode} onChange={setExpiresTimeMode} />
              </div>
              <NotificationDateTimePicker
                selected={
                  expiresTimeMode === "utc" ? toUtcDisplayDate(formData.expires_at) : formData.expires_at
                }
                onChange={(date) => {
                  const realDate = expiresTimeMode === "utc" ? fromUtcDisplayDate(date) : date;
                  handleChange("expires_at", realDate);
                  setErrors((prev) => ({
                    ...prev,
                    expires_at: expiresAtError(
                      realDate,
                      formData.category === "model_deprecation" ? formData.deprecation_at : null
                    ),
                  }));
                }}
                hasError={Boolean(errors.expires_at)}
                placeholder="No expiry"
                isClearable
                minDate={new Date()}
              />
              {errors.expires_at && <p className="text-red-500 text-xs mt-1">{errors.expires_at}</p>}
              {formData.expires_at && (
                <p className="text-xs text-gray-500 mt-1">
                  {expiresTimeMode === "utc"
                    ? `Local: ${formData.expires_at.toLocaleString()}`
                    : formatUtc(formData.expires_at)}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1.5">
                Once published, the notification is automatically removed from all user views at
                this instant (all categories).
              </p>
            </div>

            <div className="border-b border-gray-200" />

            {/* Publish timing — not part of NotificationUpdate; status
                itself only ever changes via the dedicated Publish now /
                Archive actions, never through this form. In edit mode the
                draft/now/schedule choice is hidden entirely, and the
                scheduled_for editor only appears for a notification that's
                currently scheduled (so its time can still be adjusted). */}
            {!isEditMode && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Publish
                </h3>
                <div className="flex flex-col gap-2 mb-3">
                  {[
                    { value: "draft", label: "Save as draft" },
                    { value: "now", label: "Publish now" },
                    { value: "schedule", label: "Schedule for…" },
                  ].map(({ value, label }) => (
                    <label key={value} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="radio"
                        name="publish_choice"
                        checked={formData.publish_choice === value}
                        onChange={() => handleChange("publish_choice", value)}
                        className="accent-[#2B3674]"
                      />
                      {label}
                    </label>
                  ))}
                </div>
                {formData.publish_choice === "schedule" && renderScheduledForField()}
              </div>
            )}
            {isEditMode && editTarget?.status === "scheduled" && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Scheduled for
                </h3>
                {renderScheduledForField()}
              </div>
            )}

            {/* Review */}
            <div className="bg-gray-50 rounded-lg border border-gray-100 p-4 space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                Review
              </p>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Category</span>
                <span className="font-medium text-gray-800">
                  {CATEGORY_OPTIONS.find((c) => c.value === formData.category)?.label}
                </span>
              </div>
              <div className="flex justify-between text-sm gap-4">
                <span className="text-gray-500 shrink-0">Title</span>
                <span className="font-medium text-gray-800 text-right truncate">
                  {formData.title || "—"}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Audience</span>
                <span className="font-medium text-gray-800">{audienceLabel}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Publish</span>
                <span className="font-medium text-gray-800">{publishLabel}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={() => !isSubmitting && onClose(false)}
            disabled={isSubmitting}
            className="px-5 py-2.5 text-sm font-medium rounded-full border border-gray-200 hover:bg-gray-50 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-6 py-2.5 text-sm font-medium rounded-full bg-[#2B3674] text-white hover:bg-[#232d61] transition disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting && (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            {isSubmitting
              ? "Saving…"
              : isEditMode
              ? "Save changes"
              : formData.publish_choice === "draft"
              ? "Save Draft"
              : formData.publish_choice === "schedule"
              ? "Schedule"
              : "Publish"}
          </button>
        </div>
      </div>
    </ModalPortal>
  );
};

export default CreateNotificationDrawer;
